import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { isMobile } from 'react-device-detect';
import * as Styled from './styles';
import { useEntry, DEFAULT_ENTRY } from '../../hooks/useEntry';
import { useAuth } from '../../hooks/useAuth';
import { useSweep } from '../../hooks/useSweep';
import Winners from '../../components/Winners';
import { Layout } from '../../components/Layout';
import CountDownTimer from '../../components/CountdownTimer';
import Entered from '../../components/Entered';
import { UpcomingRounds } from '../../components/UpcomingRounds';
import { checkIfExcludedFrom } from '../../utils/authUtils';
import { BetPromo } from '../../components/BetPromo';
import { useFeatures } from '../../hooks/useFeatures';
import { usePromotions } from '../../hooks/usePromotions';
import { getBettingStatus } from '../../helpers/betHelpers';
import { getPromotionConfig } from '../../helpers/promotions';
import RevealSelectionAnimation from '../../components/RevealSelectionAnimation';
import CrossPromoCards from '../../components/CrossPromoCards';
import ShareDesktop from '../../components/ShareDesktop';
import ShareMobile from '../../components/ShareMobile';
import BackToHubButton from '../../components/BackToHub';
import GoogleAnalyticsMetaDataObj from '../../helpers/googleAnalyticsHelpers/buildGoogleAnalyticsMetaDataObject';
import { sendMetadataEvent } from '../../helpers/googleAnalyticsHelpers/gaHelper';
import { didEntryMatchWinner } from './utils';
import { useSportConfig } from '../../hooks/useSportConfig';
import {
    TermsLinkWrapper,
    Itv7RacingTermsLinkWrapper,
    RacingButtonLinkWrapper
} from '../../components/LinkWrappers';

export const EnteredContainer = ({ OnLoggedOut }) => {
    const { sweep } = useSweep();
    const { entry, setEntry } = useEntry();
    const { authStore } = useAuth();
    const { features } = useFeatures();
    const { promotions } = usePromotions();
    const { config: sportTypeConfig } = useSportConfig();

    const googleAnalyticsMetadata = new GoogleAnalyticsMetaDataObj(
        authStore.user.uki_customerid,
        authStore.loggedIn,
        authStore.user['com.sbgcore.upgradable']
    );

    useEffect(() => {
        sendMetadataEvent(googleAnalyticsMetadata, 'Sweepstake');
    }, []);

    useEffect(() => {
        if (!authStore.loggedIn) {
            setEntry(DEFAULT_ENTRY);
        }
    }, [authStore.loggedIn]);

    if (!authStore.loggedIn) {
        return OnLoggedOut;
    }

    const { event } = sweep;
    const { winningSelections = [] } = event;

    const startDate = get(sweep, 'event.startDate', null);
    const eventStartDateTime = dayjs(startDate);
    const now = dayjs();
    const hasEventStarted = now.isAfter(eventStartDateTime);

    const heroBannerImageUrl = get(sweep, 'assets.heroBannerUrl', null);

    const hasWinners = winningSelections.length > 0;
    const excludeFromBetting = checkIfExcludedFrom('bet');
    const hasEntryWon = didEntryMatchWinner(winningSelections, entry.id);

    const sweepId = get(sweep, 'sweepId', '');
    const hasSeenEntry =
        localStorage.getItem(`seen_selection_${sweepId}`) !== null;
    const showEntryAnimation =
        get(features, 'entry_animation_enabled', false) &&
        !hasSeenEntry &&
        Boolean(entry.name);

    const displayCountdownTimer =
        get(features, 'countdown_timer_enabled', false) &&
        !hasWinners &&
        !hasEventStarted;

    const promotionsConfig = getPromotionConfig({
        promotions,
        hasEventStarted
    });

    const displayBettingLinks = get(features, 'betting_links_enabled', false);
    const isLoggedIn = get(authStore, 'loggedIn', false);

    const bettingEnabled = getBettingStatus(
        isLoggedIn,
        displayBettingLinks,
        !excludeFromBetting
    );

    const shareEnabled = get(features, 'share_enabled', false);

    return (
        <EnteredPage
            showEntryAnimation={showEntryAnimation}
            loggedIn={authStore.loggedIn}
            userName={authStore.user.given_name}
            excludeFromBetting={excludeFromBetting}
            sweep={sweep}
            heroBannerUrl={heroBannerImageUrl}
            hasEventStarted={hasEventStarted}
            hasWinners={hasWinners}
            winningSelections={winningSelections}
            entry={entry}
            TermsLink={TermsLinkWrapper}
            Itv7TermsLink={Itv7RacingTermsLinkWrapper}
            ButtonLink={RacingButtonLinkWrapper}
            didEntryWin={hasEntryWon}
            displayCountdownTimer={displayCountdownTimer}
            bettingEnabled={bettingEnabled}
            promotionsConfig={promotionsConfig}
            shareEnabled={shareEnabled}
            sportTypeConfig={sportTypeConfig}
        />
    );
};

export const EnteredPage = ({
    showEntryAnimation,
    loggedIn,
    userName,
    excludeFromBetting,
    sweep,
    heroBannerUrl,
    hasEventStarted,
    hasWinners,
    winningSelections,
    entry,
    didEntryWin,
    TermsLink,
    Itv7TermsLink,
    displayCountdownTimer,
    bettingEnabled,
    promotionsConfig,
    shareEnabled,
    sportTypeConfig
}) => {
    const title = get(sweep, 'name', '-');
    const prize = get(sweep, 'prize', '-');
    const eventName = get(sweep, 'event.name', '');
    const startDate = get(sweep, 'event.startDate', null);
    const allocationDate = get(sweep, 'allocationDate', null);

    const shareCopy = sportTypeConfig.labels.shareMessage(title, prize);
    const shareTitle = sportTypeConfig.labels.shareTitle(prize);
    const showPlayItv = sportTypeConfig.features.hasPlayItvCta;
    const showUpcomingRounds = sportTypeConfig.features.hasUpcomingRounds;
    const sportType = get(sportTypeConfig, 'type', 'racing');

    return (
        <>
            {showEntryAnimation && (
                <RevealSelectionAnimation
                    title={title}
                    entry={entry}
                    sweepId={get(sweep, 'sweepId')}
                />
            )}

            <Layout
                loggedIn={loggedIn}
                userName={userName}
                sportType={get(sweep, 'sportType')}
                heroBannerUrl={heroBannerUrl}
            >
                {displayCountdownTimer && (
                    <Styled.CountDownTimerWrapper>
                        {startDate && (
                            <CountDownTimer
                                name={eventName}
                                date={new Date(startDate).getTime()}
                            />
                        )}
                    </Styled.CountDownTimerWrapper>
                )}
                <BackToHubButton />
                <Styled.ContentWrapper>
                    {hasWinners ? (
                        <Winners
                            title={title}
                            eventName={eventName}
                            winners={winningSelections}
                            didEntryWin={didEntryWin}
                            entry={entry}
                            TermsLink={TermsLink}
                            Itv7TermsLink={Itv7TermsLink}
                            prize={prize}
                        />
                    ) : (
                        <Entered
                            title={title}
                            eventName={eventName}
                            prize={prize}
                            hasEventStarted={hasEventStarted}
                            entry={entry}
                            excludeFromBetting={excludeFromBetting}
                            startDate={startDate}
                            TermsLink={TermsLink}
                            bettingEnabled={bettingEnabled}
                            allocationDate={allocationDate}
                        />
                    )}
                    {shareEnabled && !hasWinners && !hasEventStarted && (
                        <Styled.ShareWrapper>
                            {isMobile ? (
                                <ShareMobile
                                    copy={shareCopy}
                                    title={shareTitle}
                                />
                            ) : (
                                <ShareDesktop
                                    copy={shareCopy}
                                    emailSubject={shareTitle}
                                />
                            )}
                        </Styled.ShareWrapper>
                    )}
                    {bettingEnabled && (
                        <Styled.BetPromoWrapper>
                            <BetPromo promotionsConfig={promotionsConfig} />
                        </Styled.BetPromoWrapper>
                    )}
                    {showPlayItv && !hasWinners && (
                        <>
                            <Styled.PlayItv7Heading>
                                More from ITV7
                            </Styled.PlayItv7Heading>
                            <Styled.PlayItv7Wrapper>
                                <CrossPromoCards
                                    currentSport={sportType}
                                    buttonText="ENTER FOR FREE"
                                    TermsLink={Itv7TermsLink}
                                />
                            </Styled.PlayItv7Wrapper>
                        </>
                    )}
                    {showUpcomingRounds && !hasWinners && !hasEventStarted && (
                        <Styled.UpComingRoundsWrapper>
                            <UpcomingRounds />
                        </Styled.UpComingRoundsWrapper>
                    )}
                </Styled.ContentWrapper>
            </Layout>
        </>
    );
};

EnteredPage.defaultProps = {
    userName: null,
    didEntryWin: false,
    entry: {},
    heroBannerUrl: null,
    sportTypeConfig: null
};

EnteredPage.propTypes = {
    showEntryAnimation: PropTypes.bool.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    userName: PropTypes.string,
    excludeFromBetting: PropTypes.bool.isRequired,
    sweep: PropTypes.shape({
        prize: PropTypes.string,
        name: PropTypes.string,
        sweepId: PropTypes.string,
        allocationDate: PropTypes.string,
        event: PropTypes.shape({
            name: PropTypes.string,
            startDate: PropTypes.string
        })
    }).isRequired,
    hasEventStarted: PropTypes.bool.isRequired,
    hasWinners: PropTypes.bool.isRequired,
    winningSelections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    entry: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        metaData: PropTypes.shape({})
    }),
    didEntryWin: PropTypes.bool,
    TermsLink: PropTypes.func.isRequired,
    Itv7TermsLink: PropTypes.func.isRequired,
    heroBannerUrl: PropTypes.string,
    displayCountdownTimer: PropTypes.bool.isRequired,
    bettingEnabled: PropTypes.bool.isRequired,
    promotionsConfig: PropTypes.shape({
        enabled: PropTypes.bool,
        eventPath: PropTypes.string,
        img: PropTypes.shape({
            mobile: PropTypes.string,
            desktop: PropTypes.string
        })
    }).isRequired,
    shareEnabled: PropTypes.bool.isRequired,
    sportTypeConfig: PropTypes.shape({
        labels: PropTypes.shape({
            shareMessage: PropTypes.func,
            shareTitle: PropTypes.func
        }),
        features: PropTypes.shape({
            hasPlayItvCta: PropTypes.bool,
            hasUpcomingRounds: PropTypes.bool
        })
    })
};

EnteredContainer.propTypes = {
    OnLoggedOut: PropTypes.node.isRequired
};

export default EnteredContainer;
